import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { Message } from 'primereact/message'
import { OverlayPanel } from 'primereact/overlaypanel'
import { ProgressSpinner } from 'primereact/progressspinner'
import { InputTextarea } from 'primereact/inputtextarea'

import useText from '../../../../../common/hooks/useText'
import Text from '../../../../../ui/Text'
import { TooltipIcon } from '../../../../../lib/icons/tooltip-icon'
import {
    ChargePoint,
    LoadBalancerGroup,
    useLoadBalancerGroupValidateChargePointImportsMutation,
    useLoadBalancerGroupImportChargePointsMutation,
    ChargePointImport,
} from '../../../../../common/redux/api/web-api'
import { useValidateImportChargePointsinLBGroupMutation } from '../../../../../admin/redux/api/chargePointsApi'
import InlineAlert, { AlertType } from '../../../../../common/components/InlineAlert'
import { useAdaptConnectorNaming } from '../../../../../lib/adaptConnectorNaming'
import { Table } from '../../../../../common/uikit/Table'
import { useToast } from '../../../../../common/uikit/ToastProvider'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { SelectButton } from '../../../../../common/uikit/SelectButton'
import { LoadBalancerChargePointSettingsDialog } from '../load-balancer-charge-point-settings-dialog'

type ImportChargePointToLBType = {
    visible: boolean
    setVisible: (show: boolean) => void
    group: LoadBalancerGroup
    chargePointsInGroup: Array<ChargePoint>
    updateChargePoints: () => void
}

type ChargePointImportRow = ChargePointImport & {
    isSelected: boolean
}

type ImportMode = 'list' | 'paste'

const lbTypes = { Unknown: 'Unknown', Invalid: 'Invalid', None: 'None', Internal: 'Internal' }

type ChargePointImportErrorButtonProps = {
    chargePointId?: string | null
    errorMessages: string[]
    ariaLabel: string
}

const ChargePointImportErrorButton = ({
    chargePointId,
    errorMessages,
    ariaLabel,
}: ChargePointImportErrorButtonProps) => {
    const overlayPanel = useRef<OverlayPanel>(null)

    return (
        <>
            <Button
                type="button"
                icon="pi pi-exclamation-triangle"
                severity="warning"
                text
                rounded
                size="small"
                aria-label={ariaLabel}
                data-testid={`toggle-import-error-${chargePointId}`}
                onClick={(event) => overlayPanel.current?.toggle(event)}
                pt={{ icon: { className: 'text-xl' } }}
            />
            <OverlayPanel
                ref={overlayPanel}
                className="max-w-20rem"
                closeOnEscape
                data-testid={`import-error-tooltip-${chargePointId}`}
            >
                <div className="text-yellow-900 text-sm p-2" data-testid={`import-error-message-${chargePointId}`}>
                    <ul className="list-none p-0 m-0">
                        {errorMessages.map((errorMessage, index) => (
                            <li key={`${chargePointId}-${index}`}>{errorMessage}</li>
                        ))}
                    </ul>
                </div>
            </OverlayPanel>
        </>
    )
}

export const LoadBalancerImportChargePointDialog = ({
    visible,
    setVisible,
    group,
    chargePointsInGroup,
    updateChargePoints,
}: ImportChargePointToLBType): ReactElement => {
    const text = useText()
    const toast = useToast()
    const [listChargePoints, setListChargePoints] = useState<Array<ChargePointImport>>([])
    const [pastedChargePoints, setPastedChargePoints] = useState<Array<ChargePointImport>>([])
    const [selectedChargePointIds, setSelectedChargePointIds] = useState<string[]>(() => [])
    const [validationError, setValidationError] = useState<string>('')
    const [showLBSettings, setShowLBSettings] = useState(false)
    const [pastedLines, setPastedLines] = useState<string>('')
    const [showInfo, setShowInfo] = useState(false)
    const [importMode, setImportMode] = useState<ImportMode>('list')

    const adaptConnectorNaming = useAdaptConnectorNaming()
    const adaptErrorModalLBAddCharger = (err?: string): string => {
        let errorMsg = ''
        const name = err?.slice(0, err.indexOf(':'))

        switch (err) {
            case 'Charger: msgLbOfflineCurrentNotset':
            case 'Socket: msgLbOfflineCurrentNotset':
            case 'Left socket: msgLbOfflineCurrentNotset':
            case 'Right socket: msgLbOfflineCurrentNotset':
                errorMsg = text('msgErrorAddChargersLBOffineCurrentNotSet', {
                    connectorType: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgLbOfflinePhaseNotset':
            case 'Socket: msgLbOfflinePhaseNotset':
            case 'Left socket: msgLbOfflinePhaseNotset':
            case 'Right socket: msgLbOfflinePhaseNotset':
                errorMsg = text('msgErrorAddChargersLBOffinePhaseNotSet', {
                    connectorType: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgInstallationCurrentHigher':
            case 'Socket: msgInstallationCurrentHigher':
            case 'Left connector: msgInstallationCurrentHigher':
            case 'Right connector: msgInstallationCurrentHigher':
            case 'Left socket: msgInstallationCurrentHigher':
            case 'Right socket: msgInstallationCurrentHigher':
                errorMsg = text('msgInstallationCurrentHigherThanMaxCurrent', {
                    ConnectorName: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgInstallationCurrentLower':
            case 'Socket: msgInstallationCurrentLower':
            case 'Left socket: msgInstallationCurrentLower':
            case 'Right socket: msgInstallationCurrentLower':
                errorMsg = text('msgInstallationCurrentLowerThanMinCurrent', {
                    ConnectorName: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgErrorLbOfflineCurrentHigher':
            case 'Socket: msgErrorLbOfflineCurrentHigher':
            case 'Left socket: msgErrorLbOfflineCurrentHigher':
            case 'Right socket: msgErrorLbOfflineCurrentHigher':
                errorMsg = text('msgErrorLBOfflineCurrentHigher', {
                    connectorType: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgUserCurrentHigher':
            case 'Socket: msgUserCurrentHigher':
            case 'Left socket: msgUserCurrentHigher':
            case 'Right socket: msgUserCurrentHigher':
                errorMsg = text('msgUserCurrentHigherThanInstallationCurrent', {
                    ConnectorName: adaptConnectorNaming(name),
                })
                break
            case 'Charger: msgUserCurrentLower':
            case 'Socket: msgUserCurrentLower':
            case 'Left socket: msgUserCurrentLower':
            case 'Right socket: msgUserCurrentLower':
                errorMsg = text('msgUserCurrentLowerThanMinCurrent', {
                    ConnectorName: adaptConnectorNaming(name),
                })
                break
            case 'Connected to ocpp server which is not supported at the moment':
                errorMsg = text('msgErrorAddChargersLBConnectedNotSupported')
                break
            case 'msgLbGroupChargerEarthingSystemMismatch':
                errorMsg = text('msgLbGroupChargerEarthingSystemMismatch')
                break
            case 'Socket: msgPhaseInvalidForEarthingSystem':
                errorMsg = text('msgPhaseInvalidForEarthingSystem')
                break
            default:
                err && err?.length > 0 ? (errorMsg = err) : (errorMsg = '')
        }

        return errorMsg
    }

    /** Get organisation's chargepoints **/

    const [validateImportChargePointInCompany, { isLoading }] = useLoadBalancerGroupValidateChargePointImportsMutation(
        {}
    )
    const [validateChargePointbyPaste] = useValidateImportChargePointsinLBGroupMutation()
    const [importChargePoints] = useLoadBalancerGroupImportChargePointsMutation({})

    useEffect(() => {
        if (!visible) {
            return
        }

        setPastedChargePoints([])
        setListChargePoints([])
        setSelectedChargePointIds([])
        setValidationError('')
        setShowLBSettings(false)
        setPastedLines('')
        setShowInfo(false)
        setImportMode('list')
    }, [visible])

    useEffect(() => {
        const validateImportChargePointsList = async () => {
            if (!visible || group.id === undefined) {
                return
            }

            try {
                const response = await validateImportChargePointInCompany({
                    id: group.id,
                    companyId: group.companyId,
                }).unwrap()
                setListChargePoints(response)
            } catch (error) {
                toast.notifyError(text('lblLoadbalancerImportFail'))
            }
        }
        validateImportChargePointsList()
    }, [group?.companyId, group?.id, text, toast, validateImportChargePointInCompany, visible])

    /** Get chargepoint by paste **/

    const doValidateImport = async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const clipboardText = event.clipboardData.getData('text')
        const groupId = group.id?.toString()

        if (!groupId) {
            return
        }

        setPastedLines(clipboardText.trim())
        try {
            const response = await validateChargePointbyPaste({
                id: groupId,
                importData: clipboardText,
            }).unwrap()
            setPastedChargePoints(response)
            setValidationError('')
        } catch (error) {
            setValidationError(text('msgImportChargersFailed'))
        }
    }

    /******************************/

    /** Check errors in selected charge points */
    const checkErrors = () => {
        if (selectedChargePoints.length) {
            return selectedChargePoints.some((point) => point.hasError)
        } else {
            return false
        }
    }

    /*****************************************/

    /** Check if charger has errors without */
    const checkOtherErrors = (chargePoint: ChargePointImport) => {
        const validErrors = ['msgLbOfflineCurrentNotset', 'msgLbOfflinePhaseNotset', 'msgErrorLbOfflineCurrentHigher']

        return Boolean(
            chargePoint.hasError &&
                chargePoint.errorReason &&
                chargePoint.errorReason.filter((error) => !validErrors.some((element) => error.includes(element)))
                    .length > 0
        )
    }
    /*****/

    const activeChargePoints = importMode === 'paste' ? pastedChargePoints : listChargePoints
    const selectedChargePoints = activeChargePoints.filter(
        (chargePoint) => typeof chargePoint.id === 'string' && selectedChargePointIds.includes(chargePoint.id)
    )
    const lbType = chargePointsInGroup[0]?.ocppServerMode ?? selectedChargePoints[0]?.ocppServerMode ?? lbTypes.Unknown

    const isChargePointDisabled = (chargePoint: ChargePointImport) =>
        (chargePoint.ocppServerMode !== lbType && lbType !== lbTypes.Unknown) ||
        lbType === lbTypes.Invalid ||
        checkOtherErrors(chargePoint)

    const selectableChargePoints = activeChargePoints.filter((chargePoint) => !isChargePointDisabled(chargePoint))
    const allSelected =
        selectableChargePoints.length > 0 &&
        selectableChargePoints.every((chargePoint) => selectedChargePointIds.includes(chargePoint.id ?? ''))

    const resetSelection = () => {
        setSelectedChargePointIds([])
    }

    const handleImportModeChange = (nextImportMode: ImportMode) => {
        setImportMode(nextImportMode)
        setPastedLines('')
        setValidationError('')
        setPastedChargePoints([])
        resetSelection()
    }

    const isChargePointSelected = (chargePoint: ChargePointImport) =>
        selectedChargePointIds.includes(chargePoint.id ?? '')

    /** Select chargepoints */
    const updateSelectedChargePoints = (chargePoint: ChargePointImport, select: boolean) => {
        if (!chargePoint.id) {
            return
        }

        if (select) {
            setSelectedChargePointIds((previousIds) =>
                previousIds.includes(chargePoint.id!) ? previousIds : [...previousIds, chargePoint.id!]
            )
        } else {
            setSelectedChargePointIds((previousIds) => previousIds.filter((id) => id !== chargePoint.id))
        }
    }

    const toggleSelectAll = (select: boolean) => {
        if (select) {
            const selected = activeChargePoints.filter((c) => {
                if (checkOtherErrors(c)) return false
                if (lbType !== lbTypes.Unknown && lbType !== lbTypes.Invalid) {
                    return c.ocppServerMode === lbType
                } else if (selectedChargePoints.length) {
                    return selectedChargePoints[0].ocppServerMode === c.ocppServerMode
                } else {
                    return c.ocppServerMode === activeChargePoints[0]?.ocppServerMode
                }
            })

            setSelectedChargePointIds(
                selected.map((chargePoint) => chargePoint.id).filter((id): id is string => Boolean(id))
            )
        } else {
            resetSelection()
        }
    }
    /**********************/

    const doImportChargePoints = async () => {
        if (group.id === undefined) {
            return
        }

        importChargePoints({
            id: group.id,
            body: selectedChargePoints,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('lblLoadbalancerImportSuccess'))
            })
            .catch(() => toast.notifyError(text('lblLoadbalancerImportFail')))
            .finally(() => {
                updateChargePoints()
                setVisible(false)
            })
    }

    const startImport = () => {
        if (!checkErrors()) {
            doImportChargePoints()
        } else {
            setShowLBSettings(true)
        }
    }
    /***/

    const renderSelectAllCheckbox = () => (
        <Checkbox
            inputId="error-checkbox"
            checked={allSelected}
            onChange={(event) => toggleSelectAll(event.checked === true)}
            disabled={activeChargePoints.length === 0}
            pt={{ input: { 'data-testid': 'checkbox-header' } }}
        />
    )

    const renderChargePointCheckbox = (chargePoint: ChargePointImportRow) => (
        <Checkbox
            inputId={`checkbox-${chargePoint.id}`}
            disabled={isChargePointDisabled(chargePoint)}
            onChange={(event) =>
                updateSelectedChargePoints(
                    activeChargePoints.find((originalChargePoint) => originalChargePoint.id === chargePoint.id) ??
                        chargePoint,
                    event.checked === true
                )
            }
            checked={chargePoint.isSelected}
            pt={{ input: { 'data-testid': `checkbox-${chargePoint.id}` } }}
        />
    )

    const renderChargerType = (chargePoint: ChargePointImport) =>
        chargePoint.ocppServerMode !== lbTypes.None ? text('lblOcpp') : text('lblCapiField')

    const renderChargePointErrors = (chargePoint: ChargePointImport) => {
        if (!chargePoint.hasError || !chargePoint.errorReason?.length) {
            return null
        }

        const errorMessages = chargePoint.errorReason.map(adaptErrorModalLBAddCharger)

        return (
            <ChargePointImportErrorButton
                chargePointId={chargePoint.id}
                errorMessages={errorMessages}
                ariaLabel={text('lblShowImportValidationMessages')}
            />
        )
    }

    const shouldShowChargePointTable =
        (importMode === 'list' && listChargePoints.length > 0) ||
        (importMode === 'paste' && Boolean(pastedLines) && !validationError)
    const chargePointRows = activeChargePoints.map((chargePoint) => ({
        ...chargePoint,
        isSelected: isChargePointSelected(chargePoint),
    }))
    const importModeOptions = [
        { label: text('lblSelectChargersFromList'), value: 'list' as const, icon: 'pi pi-list' },
        { label: text('lblPasteChargerList'), value: 'paste' as const, icon: 'pi pi-clipboard' },
    ]

    const header = (
        <div className="flex align-items-center gap-2">
            <span>{text('lblChargersImportModal')}</span>
            {isLoading && <ProgressSpinner className="w-1rem h-1rem" strokeWidth="6" />}
            <button
                type="button"
                className="p-link inline-flex align-items-center"
                onClick={() => setShowInfo(!showInfo)}
                data-testid="info-icon"
            >
                <TooltipIcon size={22} />
            </button>
        </div>
    )

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" outlined onClick={() => setVisible(false)} />
            <Button
                label={text('btnImport')}
                type="button"
                disabled={!selectedChargePoints.length}
                onClick={() => startImport()}
            />
        </div>
    )

    return (
        <>
            {!showLBSettings && (
                <Dialog
                    header={header}
                    visible={visible}
                    onHide={() => setVisible(false)}
                    style={{ width: '60rem' }}
                    breakpoints={{ '960px': '75vw', '641px': '100vw' }}
                    footer={footer}
                >
                    {showInfo && (
                        <div
                            className="max-h-20rem overflow-y-auto p-3 border-1 border-200 border-round mb-3"
                            data-testid="info-msg"
                        >
                            <Text code="msgImportChargersFileTipVersionTwo" />
                        </div>
                    )}

                    <InlineAlert
                        text={text('msgYouCantMixChargePointsInLBGroup')}
                        alertType={AlertType.Warning}
                        data-testid="wanrning-msg"
                    />

                    <div className="flex justify-content-center mb-3">
                        <SelectButton<ImportMode>
                            id="import-mode"
                            ariaLabel={text('lblChargersImportModal')}
                            value={importMode}
                            onChange={handleImportModeChange}
                            options={importModeOptions}
                            className="inline-flex"
                            data-testid="import-mode-selector"
                        />
                    </div>

                    {validationError && <Message severity="error" text={validationError} className="mb-3" />}
                    {importMode === 'paste' && (
                        <div className="flex flex-column gap-2 mb-3">
                            <label className="text-sm font-light text-600" htmlFor="paste-chargepoints">
                                {text('lblChargersImportHeading')}
                            </label>
                            <InputTextarea
                                id="paste-chargepoints"
                                className="w-full"
                                placeholder={text('msgPasteLinesField')}
                                defaultValue={pastedLines}
                                onPaste={doValidateImport}
                                rows={6}
                                data-testid="paste-textarea"
                            />
                        </div>
                    )}

                    {shouldShowChargePointTable && (
                        <Table value={chargePointRows} dataKey="id" scrollable scrollHeight="300px">
                            <Column header={renderSelectAllCheckbox()} body={renderChargePointCheckbox} />
                            <Column header={text('lblChargerType')} body={renderChargerType} />
                            <Column field="id" header={text('lblSerialNumberColumnHeader')} />
                            <Column field="name" header={text('lblName')} />
                            <Column field="supplyCurrent" header={text('lblSupplyCurrent')} />
                            <Column header={text('lblErrors')} body={renderChargePointErrors} />
                        </Table>
                    )}
                </Dialog>
            )}
            {visible && showLBSettings && (
                <LoadBalancerChargePointSettingsDialog
                    chargePoints={selectedChargePoints}
                    showModal={setVisible}
                    group={group}
                    updateChargePointList={updateChargePoints}
                />
            )}
        </>
    )
}

export default LoadBalancerImportChargePointDialog
